import { OperatorFunction, catchError, finalize, of, pipe, retry, throwError, timer } from 'rxjs';

/** Number of times a transient failure is retried before giving up. */
const TRANSIENT_RETRY_COUNT = 2;

/** Delay between transient-failure retries, in milliseconds. */
const TRANSIENT_RETRY_DELAY_MS = 300;

/**
 * A transient failure is one worth retrying: a network error (status `0`) or a
 * 5xx — EXCEPT 503, which the API returns for deliberate maintenance mode, so
 * retrying it only hammers a backend that is intentionally down.
 *
 * Note 429 (rate limited) is deliberately NOT transient: retrying a throttled
 * request just deepens the throttle, so it surfaces as an error state instead.
 */
function isTransientFailure(status: number | undefined): boolean {
  return status === 0 || (status !== undefined && status >= 500 && status !== 503);
}

export interface SsrPageErrorHandlers {
  /** Invoked when the API responds with a genuine 404 (the page truly does not exist). */
  onNotFound: () => void;
  /** Invoked when the stream settles — on success or after a handled error. */
  onSettled?: () => void;
}

/**
 * Shared error semantics for SSR-rendered content pages.
 *
 * Content pages must not turn every failure into a 404. Under SSR a throttled
 * (429), erroring (5xx) or briefly unreachable API previously rendered the hard
 * "Oops!" not-found page on perfectly valid URLs — the 404 flash. This operator
 * centralises the correct behaviour so every page handles errors identically:
 *
 *  - 404                  → calls `onNotFound` (the page genuinely does not exist).
 *  - 429 / 5xx / network  → swallowed to `undefined` (a normal empty/error state),
 *                           never the not-found page.
 *  - transient failures   → retried first — network errors and 5xx, but NOT 429
 *                           or 503.
 *
 * Errors are matched on the `status` annotated by `httpErrorInterceptor`. The
 * source value passes through unchanged on success; a handled error completes
 * the stream with `undefined`, so consumers receive `T | undefined`.
 */
export function handleSsrPageErrors<T>({
  onNotFound,
  onSettled,
}: SsrPageErrorHandlers): OperatorFunction<T, T | undefined> {
  return pipe(
    retry({
      count: TRANSIENT_RETRY_COUNT,
      delay: (error: { status?: number }) =>
        isTransientFailure(error?.status) ? timer(TRANSIENT_RETRY_DELAY_MS) : throwError(() => error),
    }),
    catchError((error: { status?: number }) => {
      if (error?.status === 404) {
        onNotFound();
      }

      return of(undefined);
    }),
    finalize(() => onSettled?.()),
  );
}
